import * as Dialog from '@radix-ui/react-dialog';
import classNames from 'classnames';
import { IconButton } from 'components/atoms';
import useModalFocusTrap from 'hooks/useModalFocusTrap';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type FC } from 'react';
import type { IColors } from 'theme/colors';
import { XIcon } from 'utils/icons';
export type SlideOver2Props = {
  showLeftPanelUI?: boolean;
  coverRightPanel?: boolean;
  rightPanelCoverContent?: any;
  renderLeftPanel?: () => any;
  renderRightPanel: () => any;
  rightPaneClassName?: string;
  rightPaneWrapperClassName?: string;
  rightPaneWidth?: string;
  onClose?: () => void;
  color?: IColors;
  name?: string;
  shouldTrapFocus?: boolean;
};

const SlideOver2: FC<SlideOver2Props> = ({
  showLeftPanelUI,
  coverRightPanel,
  rightPanelCoverContent,
  renderLeftPanel,
  renderRightPanel,
  rightPaneClassName,
  rightPaneWrapperClassName,
  rightPaneWidth = '32.5rem',
  onClose,
  shouldTrapFocus = false,
}) => {
  const leftPanelContent = renderLeftPanel?.();

  const [contentElm, setContentElm] = useState<HTMLDivElement | null>(null);

  useModalFocusTrap({
    focusElm: contentElm,
    isActive: shouldTrapFocus,
    onDeactive: onClose,
  });

  return (
    <Dialog.Root open modal={false}>
      <Dialog.Portal forceMount>
        <Dialog.Content
          asChild
          onEscapeKeyDown={(event) => {
            if (event.defaultPrevented) return;
            onClose?.();
          }}
        >
          <motion.div
            ref={setContentElm}
            className="fixed top-0 right-0 z-[100]"
            style={{
              '--rightPaneWidth': rightPaneWidth,
            }}
            transition={{ type: 'keyframes' }}
            exit={{ opacity: 0, transition: { ease: 'easeIn' } }}
          >
            <div
              className={classNames(
                { 'w-screen': showLeftPanelUI },
                'flex absolute top-0 right-0 h-[100dvh]',
              )}
            >
              {showLeftPanelUI && (
                <div
                  key="left-panel-overlay"
                  style={{
                    '--fade-in-to-opacity': 0.7,
                    animation: 'backdrop-fade-in 0.25s ease-out forwards',
                  }}
                  className={'bg-primary absolute z-1 inset-0'}
                />
              )}
              {leftPanelContent && showLeftPanelUI ? (
                <div
                  key="left-panel"
                  style={{
                    animation: 'backdrop-fade-in 0.25s ease-out forwards',
                  }}
                  className={
                    'flex-1 relative h-[100dvh] overflow-y-auto overflow-x-hidden force-scroll-style scrollbar-none py-10'
                  }
                >
                  {leftPanelContent}
                </div>
              ) : null}
              <motion.div
                key="right-panel"
                transition={{ type: 'keyframes' }}
                initial={{ x: '100%' }}
                animate={{ x: 0, transition: { ease: 'easeOut' } }}
                exit={{ x: '100%', transition: { ease: 'easeIn' } }}
                className={classNames(
                  'relative w-[var(--rightPaneWidth)] ml-auto pr-[1rem]',
                  rightPaneWrapperClassName,
                )}
              >
                {coverRightPanel && (
                  <div className="bg-white bg-opacity-[65%] absolute inset-0 z-10 pointer-events-none">
                    {rightPanelCoverContent ? rightPanelCoverContent : null}
                  </div>
                )}
                {onClose ? (
                  <div className="absolute top-[4vh] z-[51] right-[2vh] -translate-x-1/2">
                    <IconButton
                      icon={() => (
                        <XIcon className="text-primary-700 w-4 h-4" />
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                      }}
                      className="!bg-primary-25"
                    />
                  </div>
                ) : null}
                <div
                  className={classNames(
                    'h-[95dvh] mt-[2.5vh] bg-white shadow-xl overflow-y-auto force-scroll-style rounded-xl',
                    rightPaneClassName,
                  )}
                  id="expense-right-panel"
                >
                  {renderRightPanel()}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SlideOver2;
